import { Box, BoxProps, Flex } from '@chakra-ui/react';
import { SeverityBucket } from '@investigator/shared/types';
import { ReactNode } from 'react';

export interface BarData {
  color: BoxProps['backgroundColor'];
  number: number;
  bucketLevel?: SeverityBucket | undefined;
}

export interface BarSegmentRenderProps {
  barData: BarData;
  width: string;
  index: number;
}

export interface InlineBarProps {
  barData: BarData[];
  total?: number;
  renderSegment?: (props: BarSegmentRenderProps) => ReactNode;
  minWidthPx?: number;
  containerWidthPx?: number;
}

export const calculateTotal = (
  barData: BarData[],
  providedTotal?: number,
): number => {
  return providedTotal ?? barData.reduce((acc, { number }) => acc + number, 0);
};

// Calculate the width percentage for a segment
export const calculateWidthPercentage = (
  segmentValue: number,
  total: number,
): number => {
  if (total === 0) return 0;
  return (segmentValue / total) * 100;
};

export const InlineBarChart = ({
  barData,
  total,
  renderSegment,
  minWidthPx = 5,
  containerWidthPx = 0,
}: InlineBarProps) => {
  return (
    <Flex
      gap={1}
      width={containerWidthPx ? `${containerWidthPx}px` : '100%'}
      maxWidth="100%"
      data-testid="inline-bar-chart"
      overflow="visible"
    >
      {barData.map((data, index) => {
        const widthPercent = calculateWidthPercentage(
          data.number,
          calculateTotal(barData, total),
        );
        const width = `max(${widthPercent}%, ${minWidthPx}px)`;

        if (renderSegment) {
          return renderSegment({ barData: data, width, index });
        }

        return (
          <Box
            key={`${data.color}-${index}`}
            bg={data.color}
            width={width}
            height={1}
            borderRadius={2}
          />
        );
      })}
    </Flex>
  );
};
